import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { black } from '@avangard/ui/colors';

import { EmptyList } from '@modules/layout/icons';
import { Loader } from '@modules/layout/moleculas';
import { DashboardCard } from '@modules/dashboard/moleculas';
import {
    useSummaryFiltersQuery,
    useSummaryLapsGenderStudents,
} from '@modules/summary-results/hooks';
import { SummaryResultsFilterRequest } from '@modules/summary-results/requests';
import { PlatoonsPieChart } from './platoons-pie-chart';

const Root = styled(DashboardCard)`
    min-height: 360px;
    padding-bottom: 0;
`;

const Header = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
`;

const Label = styled.div`
    color: black;
    font-size: 1.4rem;
    font-weight: 600;
`;

const Body = styled.div`
    width: 100%;
    min-height: 60px;
    overflow-x: auto;
`;

const Empty = styled.div`
    width: 100%;
    margin: 32px 0;
    text-align: center;

    > p {
        margin-top: 4px;
        color: ${black[20]};
        font-size: 1.4rem;
    }
`;

const SummaryWidgetGenderCharts = (): React.ReactElement => {
    const { t } = useTranslation(['common', 'summary']);

    const { filters } = useSummaryFiltersQuery();
    const filter = new SummaryResultsFilterRequest(filters);

    const { summaryLapsGenderStudents, loading } = useSummaryLapsGenderStudents({
        fetchPolicy: 'network-only',
        variables: { filter },
    });

    const renderedBody = React.useMemo(() => {
        if (loading) {
            return <Loader />;
        }

        if (summaryLapsGenderStudents.length !== 0) {
            return <PlatoonsPieChart data={summaryLapsGenderStudents} />;
        }

        return (
            <Empty>
                <EmptyList />
                <p>{t('common:stubs.empty')}</p>
            </Empty>
        );
    }, [summaryLapsGenderStudents, loading]);

    return (
        <Root>
            <Header>
                <Label>{t('summary:widget_gender.title')}</Label>
            </Header>

            <Body>{renderedBody}</Body>
        </Root>
    );
};

export { SummaryWidgetGenderCharts };
